import { useState, useEffect } from 'react'
import { FileQuestion, Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AuthAPI, QuestionnaireAPI } from '@/services/api'
import type { Question } from '@/types/dashboard.types'

const AGE_OPTIONS = [2, 3, 4, 5]

/** Must stay in sync with `tinytalk-backend/src/constants/questionnaire.constants.ts` `AGE_ORDER_MAP`. */
const AGE_ORDER_MAP: Record<number, number[]> = {
  2: [1, 2, 3, 4, 7, 8, 10, 11, 13, 14, 16, 19, 20, 21, 22, 24, 25, 27, 37, 38],
  3: [1, 2, 3, 4, 5, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 20, 23, 27, 28, 29, 31, 33, 36, 37, 38, 39, 40],
  4: [1, 3, 4, 5, 6, 7, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 23, 26, 27, 28, 29, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40],
  5: [1, 3, 5, 6, 9, 10, 11, 12, 13, 14, 15, 17, 18, 19, 20, 21, 23, 24, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40],
}

const getQuestionTypeLabel = (type: string): string => {
  switch (type) {
    case 'MULTIPLE_CHOICE':
      return 'Multiple Choice'
    case 'YES_NO':
      return 'Yes/No'
    case 'RATING_SCALE':
      return 'Rating Scale'
    case 'FREE_TEXT':
      return 'Free Text'
    case 'NUMBER':
      return 'Number'
    default:
      return type
  }
}

const getQuestionTypeColor = (type: string): string => {
  switch (type) {
    case 'MULTIPLE_CHOICE':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200'
    case 'YES_NO':
      return 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200'
    case 'RATING_SCALE':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-950/50 dark:text-purple-200'
    case 'FREE_TEXT':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-950/50 dark:text-orange-200'
    case 'NUMBER':
      return 'bg-muted text-foreground'
    default:
      return 'bg-muted text-foreground'
  }
}

export const QuestionnaireViewer = () => {
  const [selectedAgeFilter, setSelectedAgeFilter] = useState<number | 'all'>('all')
  const [user, setUser] = useState<any>(null)
  const [questionnaire, setQuestionnaire] = useState<any>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [u, qData] = await Promise.all([
          AuthAPI.me(),
          QuestionnaireAPI.getActiveQuestionnaire()
        ])
        setUser(u.user)
        setQuestionnaire(qData)
        // Extract flat question list from questionsByDomain
        const allQuestions: Question[] = Object.values(
          (qData as any).questionsByDomain ?? {}
        ).flat() as Question[]
        setQuestions(allQuestions)
      } catch (err) {
        console.error('Failed to load questionnaire', err)
        setError('Hindi ma-load ang questionnaire. Subukang muli.')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const filteredQuestions = questions.filter((question) => {
    if (selectedAgeFilter === 'all') {
      return true
    }

    const allowedOrders = AGE_ORDER_MAP[selectedAgeFilter]
    if (allowedOrders && allowedOrders.length > 0) {
      return allowedOrders.includes(question.order)
    }

    return (question.ageYearsVisible || []).includes(selectedAgeFilter)
  })

  const visibleQuestionCount = filteredQuestions.length

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error || !questionnaire) {
    return (
      <div className="flex flex-col items-center py-20 text-center">
        <p className="text-red-500 font-medium">{error || 'Walang makita na questionnaire.'}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="animate-in slide-in-from-left duration-700">
        <h2 className="text-2xl font-bold">{questionnaire.title}</h2>
        <p className="text-muted-foreground mt-1">{questionnaire.description}</p>
        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <FileQuestion className="h-4 w-4" aria-hidden="true" />
            <span>{visibleQuestionCount} Questions</span>
          </div>
        </div>
      </div>

      {/* Age Filter */}
      <div className="flex animate-in slide-in-from-top flex-col gap-2 duration-700 sm:flex-row sm:items-center sm:gap-3">
        <span className="shrink-0 text-sm text-muted-foreground">Filter by age:</span>
        <Select
          value={String(selectedAgeFilter)}
          onValueChange={(value) => setSelectedAgeFilter(value === 'all' ? 'all' : Number(value))}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Select age" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ages</SelectItem>
            {AGE_OPTIONS.map((age) => (
              <SelectItem key={age} value={String(age)}>
                {age} years old
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Age Info */}
      {selectedAgeFilter !== 'all' && (
        <Card className="bg-blue-50 border-blue-200 animate-in slide-in-from-bottom duration-700 dark:bg-primary/10 dark:border-primary/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Age {selectedAgeFilter} years old
            </CardTitle>
            <CardDescription>
              Showing only questions that are visible for this age.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Questions List */}
      <div className="space-y-4 animate-in slide-in-from-bottom duration-700">
        {filteredQuestions.length === 0 ? (
          <Card className="border-transparent bg-card shadow-sm dark:border dark:border-border">
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground">No questions match the selected age.</p>
            </CardContent>
          </Card>
        ) : (
          filteredQuestions.map((question) => (
            <Card key={question.id} className="border-transparent bg-card shadow-sm dark:border dark:border-border">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                    {question.order}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge className={getQuestionTypeColor(question.questionType)}>
                        {getQuestionTypeLabel(question.questionType)}
                      </Badge>
                      {selectedAgeFilter === 'all' ? (
                        <Badge variant="outline" className="gap-1">
                          Ages: {(question.ageYearsVisible || []).join(', ')}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1">
                          Age: {selectedAgeFilter} years old
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-base">{question.questionText}</CardTitle>
                    {question.questionTextEnglish && (
                      <CardDescription className="mt-1 italic">
                        {question.questionTextEnglish}
                      </CardDescription>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {question.options && question.options.length > 0 && (
                  <div>
                    <h5 className="font-semibold mb-2">Answer Options:</h5>
                    <ul className="space-y-2">
                      {question.options.map((option, index) => (
                        <li
                          key={index}
                          className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border/60"
                        >
                          <div className="w-6 h-6 rounded-full bg-card border-2 border-border flex items-center justify-center text-xs font-medium">
                            {String.fromCharCode(65 + index)}
                          </div>
                          <span>{option}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {question.questionType === 'FREE_TEXT' && (
                  <div className="bg-muted/40 border border-border rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">
                      This is a free-text question. Parents can provide a detailed written response that will be analyzed by the AI language model.
                    </p>
                  </div>
                )}

                {question.questionType === 'RATING_SCALE' && (
                  <div className="bg-muted/40 border border-border rounded-lg p-4">
                    <p className="text-sm text-muted-foreground mb-2">
                      Rating Scale: 1 (Never) to 5 (Always)
                    </p>
                    <div className="flex justify-between items-center">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <div key={rating} className="flex flex-col items-center gap-1">
                          <div className="w-10 h-10 rounded-full bg-card border-2 border-primary flex items-center justify-center font-bold text-primary">
                            {rating}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {rating === 1 && 'Never'}
                            {rating === 2 && 'Rarely'}
                            {rating === 3 && 'Sometimes'}
                            {rating === 4 && 'Often'}
                            {rating === 5 && 'Always'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Summary Footer */}
      <Card className="bg-muted/30 border border-border">
        <CardContent className="py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-primary">{visibleQuestionCount}</div>
              <div className="text-sm text-muted-foreground">Visible questions</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary">{selectedAgeFilter === 'all' ? 'All ages' : `${selectedAgeFilter}`}</div>
              <div className="text-sm text-muted-foreground">Display mode</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
